/* eslint-disable complexity */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { PasswordInput } from '@/components/blocks/PasswordInput';
import {
	registerContainerSX,
	loginContainerSX,
	loginLabelSX,
	loginIsButtonSX,
} from '@/components/content/Register/styles/container';
import { useRegistration } from '@/data/Content/Registration';
import { useLocalization } from '@/data/Localization';
import { ID } from '@/data/types/Basic';
import { REG_EX } from '@/utils/address';
import { useForm } from '@/utils/useForm';
import {
	Box,
	Button,
	Divider,
	FormControl,
	FormControlLabel,
	FormGroup,
	FormHelperText,
	Grid,
	IconButton,
	Paper,
	Skeleton,
	Stack,
	Typography,
} from '@mui/material';
import { escapeRegExp } from 'lodash';
import { ChangeEvent, FC, useMemo, useState } from 'react';
import { loginButtonSX } from '@/components/content/Login/styles/button';
import { SxProps, useTheme, Theme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { Linkable } from '@/components/blocks/Linkable';
import { DpOutlinedInput } from '@/components/blocks/OutlinedInput';
import { RegistrationTextFieldSX } from '@/components/content/Register/styles/RegistrationForm';
import { BpCheckbox } from '@/components/content/Register/parts/BpCheckbox';
import { addressFormFieldsSX } from '@/components/blocks/AddressForm/styles/fields';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import {
	ModalRegisterHeaderIconBackSx,
	ModalRegisterHeaderIconCloseSx,
} from '@/components/content/Cart/parts/ModalRegister/styles/main';
import { DrawerRegisterHeaderIconBackFrameSx } from '@/components/content/Cart/parts/DrawerRegister/styles/main';
import CloseIcon from '@mui/icons-material/Close';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { PasswordValidations } from '@/components/content/Register/parts/PasswordValidations';
import {
	useDpValeExistingUser,
	useDpValeGrandRole,
	useDpValeRedeemerForRegister,
	useDpValeSmsPhoneVerification,
	useDpValeValidateToken,
	useDpValeVerifyCredentials,
} from '@/data/Content/Dpvale';
import { DpButton } from '@/components/blocks/DpLibrary/DpButton';
import { ArrowBackIos } from '@mui/icons-material';
import { Switch } from '@/utils/switch';
import { DpOutlinedOTPInput } from '@/components/blocks/OTPInput';
import { useNotifications } from '@/data/Content/Notifications';
import { useTimedBlocker } from '@/data/Content/Timer';
import { personMutatorKeyMatcher, useLogin } from '@/data/Content/Login';
import { mutate } from 'swr';
import { useDpvaleSelected } from '@/data/Content/DpvaleSelected';
import { useRegisterContext } from '@/data/context/register';
import { parseHTML } from '@/utils/parseHTML';
import { passwordValidation } from '@/utils/passwordValidation';

const errorsFormFields = {
	errorMessages: {
		errorMsg: 'Campo obligatorio',
		rules: {
			email: 'Por favor ingresa un correo electrónico válido',
			phone1: 'El teléfono debe ser de 10 dígitos',
			logonPassword: 'Mínimo 1 letra, 1 número y 8 caracteres',
		},
	},
};
interface ErrorInter {
	email: string;
	firstName: string;
	lastName: string;
	secondLastName: string;
	phone1: string;
	logonPassword: string;
	noticeOfPrivacy: string;
	lastNameMat: string;
}
export type UserRegistrationWithoutBoolean = {
	email: string;
	phone1: string;
	firstName: string;
	lastName: string;
	logonPassword: string;
	lastNameMat: string;
};

export type UserRegistrationWithoutExtraValidation = {
	firstName: string;
	lastName: string;
	lastNameMat: string;
};
const keyNameMap: Record<keyof UserRegistrationWithoutExtraValidation, string> = {
	firstName: 'firstName',
	lastName: 'lastName',
	lastNameMat: 'lastNameMat',
};

const capitalize = (text: string) => {
	if (!text) return '';
	return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

enum REGISTER_STEPS {
	LOADING = 'loading',
	BAD_TOKEN = 'badToken',
	REGISTER_FORM = 'registerForm',
	ALREADY_REGISTERED = 'alreadyRegistered',
	VERIFY_CREDENTIALS = 'verifyCredentials',
	OTP_FORM = 'otpForm',
}

export const Register: FC<{
	id: ID;
	withoutLogin?: boolean;
	onGoBack?: () => void;
	onClose?: () => void;
	adaptWith?: boolean;
	origin?: 'checkout' | 'account' | 'cart';
}> = ({ withoutLogin = false, onGoBack, onClose, adaptWith = false, origin }) => {
	const registerNLS = useLocalization('RegistrationLayout');
	const RouteLocal = useLocalization('Routes');
	const router = useNextRouter();
	const token = useMemo(() => router.query.token as string, [router.query]);
	const { loginSubmit } = useLogin();
	const { setDpvaleSelected } = useDpvaleSelected();
	const { setRegisterContext } = useRegisterContext();

	const { data: dpValeValidateToken, isLoading: isLoadingDpValeValidateToken } =
		useDpValeValidateToken(token);

	const { data: dpValeRedeemerForRegister, isLoading: isLoadingDpValeRedeemerForRegister } =
		useDpValeRedeemerForRegister(dpValeValidateToken?.idCanjeante);

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const { registrationSubmit } = useRegistration(origin ? { origin } : undefined);
	const { searchDpValeExistingUser } = useDpValeExistingUser();
	const { sendSmsPhoneVerification, smsPhoneVerification } = useDpValeSmsPhoneVerification();
	const { verifyCredentials } = useDpValeVerifyCredentials();
	const { grandRole } = useDpValeGrandRole();
	const { showErrorMessage } = useNotifications();

	const [registrationRuntimeValues, setRegistrationRuntimeValues] = useState({
		showAlertAlreadyRegistered: false,
		showAlertAlreadyRegisteredCreateNewAccount: false,
		showFormVerifyCredentials: false,
		loadingResendCode: false,
		disabledResendCode: false,
		showOtp: false,
		otp: '',
	});

	const initialRegistration = useMemo(
		() => ({
			logonId: '',
			email: dpValeRedeemerForRegister?.email.toLowerCase() || '',
			firstName: capitalize(dpValeRedeemerForRegister?.firstName || ''),
			lastName: capitalize(dpValeRedeemerForRegister?.lastName || ''),
			logonPassword: '',
			logonPasswordVerify: '',
			phone1: dpValeRedeemerForRegister?.phone || '',
			receiveEmail: true,
			rememberMe: false,
			secondLastName: capitalize(dpValeRedeemerForRegister?.secondLastName || ''),
			lastNameMat: capitalize(dpValeRedeemerForRegister?.secondLastName || ''),
			phone: dpValeRedeemerForRegister?.phone || '',
			noticeOfPrivacy: false,
		}),
		[dpValeRedeemerForRegister]
	);

	const {
		formRef,
		values: registrationValues,
		handleInputChange: handleChange,
		handleSubmit,
		submitting,
	} = useForm(initialRegistration);

	const [errors, setErrors] = useState<ErrorInter>({
		email: '',
		firstName: '',
		lastName: '',
		secondLastName: '',
		phone1: '',
		logonPassword: '',
		noticeOfPrivacy: '',
		lastNameMat: '',
	});

	const { isBlocked, remainingTime, reset } = useTimedBlocker({
		active: registrationRuntimeValues.showOtp,
		duration: 60,
	});

	const validateFields = (): boolean => {
		let isValid = true;

		const {
			noticeOfPrivacy,
			secondLastName,
			receiveEmail,
			rememberMe,
			logonPasswordVerify,
			logonId,
			phone,
			...registrationValuesString
		} = registrationValues;

		const fieldsWithError: string[] = (
			Object.keys(registrationValuesString) as Array<keyof UserRegistrationWithoutExtraValidation>
		)
			.filter((key) => registrationValuesString[key]?.trim?.() === '')
			.map((key) => keyNameMap[key] || key);

		if (
			Object.values(registrationValuesString as UserRegistrationWithoutBoolean).some(
				(value) => value?.trim?.() === ''
			)
		) {
			fieldsWithError.forEach((field) => {
				setErrors((prev) => ({
					...prev,
					[field]: errorsFormFields.errorMessages.errorMsg,
				}));
			});
			isValid = false;
		} else {
			fieldsWithError.forEach((field) => {
				setErrors((prev) => ({
					...prev,
					[field]: '',
				}));
			});
		}
		if (registrationValues?.email?.trim?.() === '') {
			setErrors((prev) => ({
				...prev,
				email: errorsFormFields.errorMessages.errorMsg,
			}));
			isValid = false;
		} else if (!REG_EX.EMAIL.test(registrationValues.email || '')) {
			setErrors((prev) => ({
				...prev,
				email: errorsFormFields.errorMessages.rules.email,
			}));
			isValid = false;
		} else {
			setErrors((prev) => ({
				...prev,
				email: '',
			}));
		}
		if (registrationValues.phone1?.trim?.() === '') {
			setErrors((prev) => ({
				...prev,
				email: errorsFormFields.errorMessages.errorMsg,
			}));
			isValid = false;
		} else if (registrationValues.phone1.length < 10) {
			setErrors((prev) => ({
				...prev,
				email: errorsFormFields.errorMessages.rules.phone1,
			}));
			isValid = false;
		} else {
			setErrors((prev) => ({
				...prev,
				phone1: '',
			}));
		}
		if (registrationValues.logonPassword?.trim?.() === '') {
			setErrors((prev) => ({
				...prev,
				logonPassword: errorsFormFields.errorMessages.errorMsg,
			}));
			isValid = false;
		} else if (!handleLetterNumberAndLengthInputChange(registrationValues.logonPassword)) {
			setErrors((prev) => ({
				...prev,
			}));
			isValid = false;
		} else {
			setErrors((prev) => ({
				...prev,
				logonPassword: '',
			}));
		}
		if (!registrationValues.noticeOfPrivacy) {
			setErrors((prev) => ({
				...prev,
				noticeOfPrivacy: errorsFormFields.errorMessages.errorMsg,
			}));
			isValid = false;
		} else {
			setErrors((prev) => ({
				...prev,
				noticeOfPrivacy: '',
			}));
		}
		return isValid;
	};

	const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
		const { name, value } = event.target;
		validateField(name, value);
	};

	// eslint-disable-next-line complexity
	const validateField = (name: string, value: string) => {
		if (name === 'email' || name === 'phone1' || name === 'logonPassword') {
			if (value?.trim?.() === '') {
				setErrors((prev) => ({
					...prev,
					[name]: errorsFormFields.errorMessages.errorMsg,
				}));
			}
			if (name === 'email') {
				if (!REG_EX.EMAIL.test(value)) {
					setErrors((prev) => ({
						...prev,
						[name]: errorsFormFields.errorMessages.rules[name],
					}));
				} else {
					setErrors((prev) => ({
						...prev,
						[name]: '',
					}));
				}
			}
			if (name === 'phone1') {
				if (value.length < 10) {
					setErrors((prev) => ({
						...prev,
						[name]: errorsFormFields.errorMessages.rules[name],
					}));
				} else {
					setErrors((prev) => ({
						...prev,
						[name]: '',
					}));
				}
			}
			if (name === 'logonPassword') {
				if (!handleLetterNumberAndLengthInputChange(value)) {
					setErrors((prev) => ({
						...prev,
					}));
				} else {
					setErrors((prev) => ({
						...prev,
						[name]: '',
					}));
				}
			}
			return;
		}
		if (value?.trim?.() === '') {
			setErrors((prev) => ({
				...prev,
				[name]: 'Campo obligatorio',
			}));
			return;
		}
		setErrors((prev) => ({
			...prev,
			[name]: '',
		}));
	};

	const handleNoticeOfPrivacyChange = (event: ChangeEvent<HTMLInputElement>) => {
		const checked = event.target.checked;

		handleChange(event);

		if (checked) {
			setErrors((prev) => ({
				...prev,
				[event.target.name]: '',
			}));
		}
	};

	const handleResendCode = async (phone?: string) => {
		setRegistrationRuntimeValues((prev) => ({
			...prev,
			loadingResendCode: true,
		}));

		try {
			await sendSmsPhoneVerification({
				phoneNumber: '52' + (phone || registrationValues.phone1),
				formOrigin: 'Registro',
				plataforma: 'Sitio',
				sender: 'dpvale',
			});
			setRegistrationRuntimeValues((prev) => ({
				...prev,
				otp: '',
			}));
		} catch {
			showErrorMessage('No se pudo enviar el código');
		} finally {
			reset();
			setRegistrationRuntimeValues((prev) => ({
				...prev,
				loadingResendCode: false,
			}));
		}
	};

	const handleSubmitForm = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		const isValid = validateFields();

		if (!isValid) {
			const firstErrorField = document.querySelector('.Mui-error');
			if (firstErrorField) {
				(firstErrorField as HTMLElement).focus();
			}
			return;
		}

		handleSubmit(async (props) => {
			if (registrationRuntimeValues.showOtp) {
				if (registrationRuntimeValues.otp.length !== 6) {
					showErrorMessage('El código OTP debe tener 6 dígitos');
					return;
				}

				try {
					const response = await smsPhoneVerification({
						phoneNumber: '52' + props.phone1,
						verificationCode: registrationRuntimeValues.otp,
						formOrigin: 'Registro',
					});

					if (!response.isValid) {
						throw new Error('El código OTP no es válido');
					}

					if (registrationRuntimeValues.showAlertAlreadyRegisteredCreateNewAccount) {
						await registrationSubmit({
							...props,
							logonId: props.phone1,
							logonPasswordVerify: props.logonPassword,
							x_idCanjeante: dpValeValidateToken?.idCanjeante.toString(),
						});

						setDpvaleSelected({
							distributorId: dpValeValidateToken?.idDistribuidor.toString(),
						});

						setRegisterContext({ dpValeRedeemerForRegister });

						await router.push({
							pathname: RouteLocal.WelcomeMidp.route.t(),
							query: {
								redeemerId: dpValeValidateToken?.idCanjeante,
								distributorId: dpValeValidateToken?.idDistribuidor,
							},
						});
						return;
					}

					if (registrationRuntimeValues.showFormVerifyCredentials) {
						if (!dpValeValidateToken?.idCanjeante) {
							showErrorMessage('No se pudo encontrar el identificador de canjeante');
							return;
						}

						try {
							await grandRole({
								logonId: props.email,
								password: props.logonPassword,
								idCanjeante: dpValeValidateToken.idCanjeante.toString(),
								phone: props.phone1,
							});

							await loginSubmit({
								logonId: props.phone1,
								logonPassword: props.logonPassword,
								rememberMe: props.rememberMe,
							});

							setDpvaleSelected({
								distributorId: dpValeValidateToken.idDistribuidor.toString(),
							});
							setRegisterContext({ dpValeRedeemerForRegister });
							await router.push({
								pathname: RouteLocal.WelcomeMidp.route.t(),
								query: {
									redeemerId: dpValeValidateToken.idCanjeante,
									distributorId: dpValeValidateToken.idDistribuidor,
								},
							});
							await mutate(personMutatorKeyMatcher(''), undefined);
						} catch {
							showErrorMessage('No se pudo verificar las credenciales');
						}
						return;
					}

					await registrationSubmit({
						...props,
						logonId: props.phone1,
						logonPasswordVerify: props.logonPassword,
						x_idCanjeante: dpValeValidateToken?.idCanjeante.toString(),
					});
					setDpvaleSelected({
						distributorId: dpValeValidateToken?.idDistribuidor.toString(),
					});
					setRegisterContext({ dpValeRedeemerForRegister });
					await router.push({
						pathname: RouteLocal.WelcomeMidp.route.t(),
						query: {
							redeemerId: dpValeValidateToken?.idCanjeante,
							distributorId: dpValeValidateToken?.idDistribuidor,
						},
					});
				} catch {
					showErrorMessage('El código OTP no es válido');
				}

				return;
			}

			if (registrationRuntimeValues.showFormVerifyCredentials) {
				try {
					await verifyCredentials({
						password: props.logonPassword,
						logonId: props.email,
					});

					await handleResendCode(props.phone1);
					setRegistrationRuntimeValues((prev) => ({
						...prev,
						showOtp: true,
						showFormVerifyCredentials: true,
					}));
				} catch {
					showErrorMessage('El código OTP no es válido');
				}

				return;
			}

			if (registrationRuntimeValues.showAlertAlreadyRegistered) {
				await handleResendCode(props.phone1);
				setRegistrationRuntimeValues((prev) => ({
					...prev,
					showOtp: true,
					showAlertAlreadyRegisteredCreateNewAccount: true,
				}));
				return;
			}

			if (
				registrationRuntimeValues.showAlertAlreadyRegistered === false &&
				dpValeRedeemerForRegister?.existingUser.logonId === registrationValues.email &&
				dpValeRedeemerForRegister?.existingUser.dportenis
			) {
				setRegistrationRuntimeValues((prev) => ({
					...prev,
					showAlertAlreadyRegistered: true,
				}));
				return;
			}

			const dpValeExistingUser = await searchDpValeExistingUser(registrationValues.email);

			if (dpValeExistingUser.dpValeUser) {
				router.push({
					pathname: RouteLocal.Login.route.t(),
					query: {
						...(router.query.flow ? { flow: router.query.flow } : {}),
						logonId: dpValeExistingUser.logonId,
						token,
						event: 'existingUser',
					},
				});
				return;
			}

			if (dpValeExistingUser.dportenisUser) {
				setRegistrationRuntimeValues((prev) => ({
					...prev,
					showAlertAlreadyRegistered: true,
				}));
				return;
			}

			await handleResendCode(props.phone1);
			setRegistrationRuntimeValues((prev) => ({
				...prev,
				showOtp: true,
				showFormVerifyCredentials: false,
				showAlertAlreadyRegistered: false,
				showAlertAlreadyRegisteredCreateNewAccount: false,
			}));
		})(event);
		onClose?.();
	};

	const handleLetterNumberAndLengthInputChange = (value: string) => {
		const hasLetter = /[a-zA-Z]/.test(value);
		const hasNumber = /[0-9]/.test(value);
		return hasLetter && hasNumber && value.length >= 8;
	};

	const handleGoBackAlertAlreadyRegistered = () => {
		setRegistrationRuntimeValues((prev) => ({
			...prev,
			showAlertAlreadyRegistered: false,
		}));
	};

	const handleGoBackVerifyCredentials = () => {
		setRegistrationRuntimeValues((prev) => ({
			...prev,
			showAlertAlreadyRegistered: true,
			showFormVerifyCredentials: false,
		}));
	};

	const handleShowFormVerifyCredentials = () => {
		setRegistrationRuntimeValues((prev) => ({
			...prev,
			showFormVerifyCredentials: true,
		}));
	};

	const handleChangeOtp = (otp: string[]) => {
		setRegistrationRuntimeValues((prev) => ({
			...prev,
			otp: otp.join(''),
		}));
	};

	const handleGoBackOtp = () => {
		setRegistrationRuntimeValues((prev) => ({
			...prev,
			showOtp: false,
		}));
	};

	const isLoading = isLoadingDpValeValidateToken || isLoadingDpValeRedeemerForRegister;
	const isRegisterValid =
		dpValeValidateToken?.isValid === true && !isLoading && dpValeRedeemerForRegister?.phone;

	const currentStep = useMemo(() => {
		if (isLoadingDpValeValidateToken || isLoadingDpValeRedeemerForRegister) {
			return REGISTER_STEPS.LOADING;
		}

		if (
			!dpValeValidateToken ||
			dpValeValidateToken?.isValid !== true ||
			!dpValeRedeemerForRegister
		) {
			return REGISTER_STEPS.BAD_TOKEN;
		}

		if (registrationRuntimeValues.showOtp) {
			return REGISTER_STEPS.OTP_FORM;
		}

		if (registrationRuntimeValues.showFormVerifyCredentials) {
			return REGISTER_STEPS.VERIFY_CREDENTIALS;
		}

		if (registrationRuntimeValues.showAlertAlreadyRegistered) {
			return REGISTER_STEPS.ALREADY_REGISTERED;
		}

		return REGISTER_STEPS.REGISTER_FORM;
	}, [
		dpValeValidateToken,
		isLoadingDpValeRedeemerForRegister,
		isLoadingDpValeValidateToken,
		registrationRuntimeValues.showAlertAlreadyRegistered,
		registrationRuntimeValues.showOtp,
		registrationRuntimeValues.showFormVerifyCredentials,
		dpValeRedeemerForRegister,
	]);

	const blockRegister = useMemo(
		() => submitting || !dpValeRedeemerForRegister?.phone,
		[submitting, dpValeRedeemerForRegister]
	);

	return (
		<Grid container spacing={0}>
			<Grid
				item
				xs={12}
				md={6}
				m="auto"
				sx={{
					maxWidth: '700px !important',
					flexBasis: isMobile ? '100% !important' : '60% !important',
					pt: { md: '60px', xs: '20px' },
					position: onGoBack && 'relative',
				}}
			>
				{onGoBack ? (
					<IconButton
						onClick={onGoBack}
						sx={{
							...DrawerRegisterHeaderIconBackFrameSx,
							position: 'absolute',
							top: !adaptWith ? '71px' : '0px',
							left: !adaptWith ? '-6px' : '-34px',
						}}
					>
						<ArrowBackIosIcon sx={ModalRegisterHeaderIconBackSx} />
					</IconButton>
				) : null}

				{onClose ? (
					<IconButton
						onClick={onClose}
						sx={{
							...ModalRegisterHeaderIconCloseSx,
							position: 'absolute',
							top: !adaptWith ? '71px' : '0px',
							right: !adaptWith ? '5px' : '-20px',
						}}
					>
						<CloseIcon />
					</IconButton>
				) : null}
				<Paper
					sx={registerContainerSX({ isMobile, onGoBack, adaptWith })}
					component="form"
					onSubmit={handleSubmitForm}
					ref={formRef}
					noValidate
				>
					<Stack spacing={2}>
						{Switch(currentStep)
							.case(REGISTER_STEPS.ALREADY_REGISTERED, () => null)
							.case(REGISTER_STEPS.VERIFY_CREDENTIALS, () => null)
							.case(REGISTER_STEPS.OTP_FORM, () => null)
							.defaultTo(() => (
								<>
									<Typography
										component={'h1'}
										sx={{
											fontSize: { md: 'var(--font-size-xl)', xs: 'var(--font-size-lg)' },
											fontWeight: '800',
											letterSpacing: '0px',
											color: '#2C2C2C',
											textTransform: 'uppercase',
											opacity: 1,
										}}
									>
										{isLoading || isRegisterValid ? 'REGÍSTRATE' : 'No podemos registrarte'}
									</Typography>
									<Typography
										component={'h2'}
										sx={{
											fontSize: 'var(--font-size-sm)',
											fontWeight: 'normal',
											letterSpacing: '0px',
											color: '#2C2C2C',
											opacity: 1,
											mt: '0px !important',
										}}
									>
										{isLoading || isRegisterValid ? (
											'Llena tus datos y disfruta de los beneficios'
										) : (
											<>
												<Linkable
													variant="primary"
													type="link"
													href={{
														pathname: RouteLocal.Login.route.t(),
														query: router.query.flow ? { flow: router.query.flow } : {},
													}}
												>
													{'Inicia sesión'}
												</Linkable>
												{
													'  o comunícate con tu distribuidor dpvale para que puedas registrarte y disfrutar de todos nuestros beneficios'
												}
											</>
										)}
									</Typography>
								</>
							))}

						{Switch(currentStep)
							.case(REGISTER_STEPS.LOADING, () => <Skeleton variant="rectangular" height={350} />)
							.case(REGISTER_STEPS.BAD_TOKEN, () => null)
							.case(REGISTER_STEPS.REGISTER_FORM, () => (
								<>
									<Grid container>
										<Grid item xs={12} sm={6} pr={{ xs: 0, sm: 1 }}>
											<DpOutlinedInput
												variant="outlined"
												margin="none"
												required
												fullWidth
												name="phone1"
												type="number"
												id="WC_UserRegistrationAddForm_AddressEntryForm_FormInput_phone1_1"
												label={registerNLS.Phone.t()}
												error={!!errors.phone1}
												helperText={errors.phone1}
												sx={addressFormFieldsSX}
												value={registrationValues.phone1}
												disabled
												inputProps={{
													maxLength: 10,
												}}
											/>
										</Grid>
										<Grid item xs={12} sm={6} pl={{ xs: 0, sm: 1 }} mt={{ xs: 2, sm: 0 }}>
											<DpOutlinedInput
												variant="outlined"
												margin="none"
												required
												fullWidth
												name="firstName"
												id="WC_UserRegistrationAddForm_NameEntryForm_FormInput_firstName_1"
												label={registerNLS.FirstName.t()}
												error={!!errors.firstName}
												helperText={errors.firstName}
												value={registrationValues.firstName}
												sx={addressFormFieldsSX}
												disabled
												inputProps={{
													maxLength: 40,
												}}
											/>
										</Grid>
									</Grid>
									<Grid container>
										<Grid item xs={12} sm={6} pr={{ xs: 0, sm: 1 }}>
											<DpOutlinedInput
												variant="outlined"
												margin="none"
												required
												fullWidth
												name="lastName"
												id="WC_UserRegistrationAddForm_NameEntryForm_FormInput_lastName_1"
												label={registerNLS.LastName.t()}
												error={!!errors.lastName}
												helperText={errors.lastName}
												sx={addressFormFieldsSX}
												value={registrationValues.lastName}
												disabled
												inputProps={{
													maxLength: 40,
												}}
											/>
										</Grid>
										<Grid item xs={12} sm={6} pl={{ xs: 0, sm: 1 }} mt={{ xs: 2, sm: 0 }}>
											<DpOutlinedInput
												variant="outlined"
												margin="none"
												required
												fullWidth
												name="lastNameMat"
												label={registerNLS.SecondLastName.t()}
												error={!!errors.lastNameMat}
												helperText={errors.lastNameMat}
												sx={addressFormFieldsSX}
												value={registrationValues.lastNameMat}
												disabled
												inputProps={{
													maxLength: 40,
												}}
											/>
										</Grid>
									</Grid>
									<Grid container>
										<Grid item xs={12} sm={6} pr={{ xs: 0, sm: 1 }}>
											<DpOutlinedInput
												variant="outlined"
												margin="none"
												fullWidth
												required
												name="email"
												autoComplete="email"
												id="WC_UserRegistrationAddForm_FormInput_logonId_In_Register_1_1"
												label={registerNLS.Email.t()}
												autoFocus
												onBlur={handleOnBlur}
												error={!!errors.email}
												helperText={errors.email}
												value={registrationValues.email}
												sx={addressFormFieldsSX}
												onChange={handleChange}
												type="email"
												inputProps={{
													maxLength: 50,
													pattern: REG_EX.EMAIL.source,
												}}
											/>
										</Grid>

										<Grid item xs={12} sm={6} pl={{ xs: 0, sm: 1 }} mt={{ xs: 2, sm: 0 }}>
											<PasswordInput
												sx={
													{
														...(RegistrationTextFieldSX as SxProps<Theme>),
														...(addressFormFieldsSX as SxProps<Theme>),
													} as SxProps<Theme>
												}
												required
												name="logonPassword"
												label={registerNLS.Password.t()}
												onBlur={handleOnBlur as () => void}
												error={!!errors.logonPassword}
												helperText={errors.logonPassword}
												value={registrationValues.logonPassword}
												onChange={handleChange}
												inputProps={{
													maxLength: 100,
													pattern: escapeRegExp(registrationValues.logonPassword),
												}}
											/>
											{registrationValues.logonPassword ? (
												<Box sx={{ mb: -2 }}>
													<PasswordValidations
														validations={{
															...passwordValidation(registrationValues.logonPassword),
															isMobile,
														}}
													/>
												</Box>
											) : null}
										</Grid>
									</Grid>
									<Grid container>
										<Grid item xs={12}>
											<FormControlLabel
												sx={{ fontSize: 'var(--font-size-small)' }}
												control={
													<BpCheckbox
														name="receiveEmail"
														checked={registrationValues.receiveEmail}
														onChange={handleChange}
													/>
												}
												label={
													<Typography
														component={'span'}
														sx={{ fontSize: 'var(--font-size-small)' }}
													>
														{registerNLS.TextContent.t()}
													</Typography>
												}
											/>
										</Grid>
										<Grid item xs={12} mt={'8px'}>
											<FormControl required error={!!errors.noticeOfPrivacy}>
												<FormGroup>
													<FormControlLabel
														control={
															<BpCheckbox
																name="noticeOfPrivacy"
																checked={registrationValues.noticeOfPrivacy}
																onChange={handleNoticeOfPrivacyChange}
															/>
														}
														label={
															<Typography
																component="span"
																sx={{ fontSize: 'var(--font-size-small)' }}
															>
																{'He leído y consentido el'}{' '}
																<Linkable
																	component="a"
																	type="link"
																	variant="primary"
																	href="https://www.dportenis.mx/aviso-privacidad"
																	target="_blank"
																>
																	{'Aviso de privacidad'}
																</Linkable>
															</Typography>
														}
													/>
												</FormGroup>
												{errors.noticeOfPrivacy ? (
													<FormHelperText
														sx={{
															marginLeft: '0px !important',
															fontSize: 'var(--font-size-caption)',
														}}
													>
														{'Campo obligatorio'}
													</FormHelperText>
												) : null}
											</FormControl>
										</Grid>
									</Grid>
									<Stack spacing={2}>
										<DpButton
											type="submit"
											sx={loginButtonSX}
											disabled={blockRegister}
											data-testid="button-register-submit"
											id="button-register-submit"
										>
											{registerNLS.RegisterComplete.t()}
										</DpButton>
										<DpButton
											variant="text"
											sx={{
												textDecoration: 'underline',
												fontSize: 'var(--font-size-sm)',
											}}
										>
											{'¿No es tu número de teléfono?'}
										</DpButton>
									</Stack>
								</>
							))
							.case(REGISTER_STEPS.ALREADY_REGISTERED, () => (
								<>
									<Typography
										component={'h1'}
										sx={{
											fontSize: { md: 'var(--font-size-xl)', xs: 'var(--font-size-lg)' },
											fontWeight: '800',
											letterSpacing: '0px',
											color: '#2C2C2C',
											textTransform: 'uppercase',
											opacity: 1,
										}}
									>
										<ArrowBackIos
											sx={{
												fontSize: 'var(--font-size-lg)',
												color: '#2C2C2C',
												cursor: 'pointer',
											}}
											onClick={handleGoBackAlertAlreadyRegistered}
										/>
										{'Te conocemos'}
									</Typography>
									<Typography
										sx={{
											fontSize: 'var(--font-size-sm)',
											color: '#2C2C2C',
										}}
									>
										{
											'Sabemos que ya eres un cliente de dportenis, puedes continuar tu registro de forma normal ó usar tu cuenta existente'
										}
									</Typography>
									<Typography
										sx={{
											fontSize: 'var(--font-size-sm)',
											color: '#424242',
										}}
									>
										{parseHTML('Deseo <b>continuar mi registro</b> con una nueva cuenta')}
									</Typography>
									<DpButton
										type="submit"
										sx={loginButtonSX}
										disabled={blockRegister}
										data-testid="button-register-submit"
										id="button-register-submit"
									>
										{'Continuar con cuenta nueva'}
									</DpButton>
									<Divider />
									<Typography
										sx={{
											fontSize: 'var(--font-size-sm)',
											color: '#424242',
										}}
									>
										{parseHTML('<b>Quiero usar mi cuenta</b> de ')}
										<Linkable variant="primary" href="https://www.dportenis.mx">
											{'dportenis.mx'}
										</Linkable>
									</Typography>
									<DpButton
										variant="text"
										sx={{ textDecoration: 'underline', fontSize: 'var(--font-size-sm)' }}
										onClick={handleShowFormVerifyCredentials}
									>
										{'Usar mi cuenta existente'}
									</DpButton>
								</>
							))
							.case(REGISTER_STEPS.OTP_FORM, () => (
								<Stack spacing={2} pb={2}>
									<Typography
										component={'h1'}
										sx={{
											fontSize: { md: 'var(--font-size-xl)', xs: 'var(--font-size-lg)' },
											fontWeight: '800',
											letterSpacing: '0px',
											color: '#2C2C2C',
											textTransform: 'uppercase',
											opacity: 1,
										}}
									>
										<ArrowBackIos
											sx={{
												fontSize: 'var(--font-size-md)',
												color: '#2C2C2C',
												cursor: 'pointer',
											}}
											onClick={handleGoBackOtp}
										/>
										{'Ya casi terminas'}
									</Typography>
									<Typography
										sx={{
											fontSize: 'var(--font-size-sm)',
											color: '#2C2C2C',
										}}
									>
										{'Hemos enviado un código a tu celular, captúralo para activar tu cuenta.'}
									</Typography>
									<Typography
										sx={{
											fontSize: 'var(--font-size-sm)',
											color: '#424242',
										}}
									>
										{'Ingresa el código aqui:'}
									</Typography>
									<Stack spacing={2} justifyContent="center" alignItems="center">
										<DpOutlinedOTPInput
											value={registrationRuntimeValues.otp}
											otpLength={6}
											onChange={handleChangeOtp}
										/>
										<DpButton
											type="submit"
											sx={{
												width: { xs: '100%', md: '300px' },
												borderRadius: '22px',
												paddingTop: '9px',
												paddingBottom: '9px',
												fontSize: '15px',
											}}
											disabled={blockRegister}
											data-testid="button-register-submit"
											id="button-register-submit"
										>
											{'Validar mi cuenta'}
										</DpButton>
										<Stack spacing={1}>
											<Typography
												sx={{
													fontSize: 'var(--font-size-sm)',
													color: '#424242',
												}}
											>
												{'¿Aún no llega tu código? No te preocupes, podrás generar uno nuevo'}
											</Typography>
											<DpButton
												variant="text"
												sx={{ textDecoration: 'underline', fontSize: 'var(--font-size-sm)' }}
												onClick={() =>
													!(registrationRuntimeValues.loadingResendCode || isBlocked) &&
													handleResendCode()
												}
											>
												{isBlocked ? `Reenviar código (${remainingTime})` : 'Reenviar código'}
											</DpButton>
										</Stack>
									</Stack>
								</Stack>
							))
							.case(REGISTER_STEPS.VERIFY_CREDENTIALS, () => (
								<>
									<Typography
										component={'h1'}
										sx={{
											fontSize: { md: 'var(--font-size-xl)', xs: 'var(--font-size-lg)' },
											fontWeight: '800',
											letterSpacing: '0px',
											color: '#2C2C2C',
											textTransform: 'uppercase',
											opacity: 1,
										}}
									>
										<ArrowBackIos
											sx={{
												fontSize: 'var(--font-size-md)',
												color: '#2C2C2C',
												cursor: 'pointer',
											}}
											onClick={handleGoBackVerifyCredentials}
										/>
										{'Te conocemos'}
									</Typography>
									<Typography
										sx={{
											fontSize: 'var(--font-size-sm)',
											color: '#2C2C2C',
										}}
									>
										{
											'Sabemos que ya eres un cliente de dportenis, puedes continuar tu registro con tu cuenta existente'
										}
									</Typography>
									<Typography
										sx={{
											fontSize: 'var(--font-size-sm)',
											color: '#2C2C2C',
										}}
									>
										{'Usa tus credenciales para acceder con tu cuenta'}
									</Typography>
									<Stack pb={2} spacing={2}>
										<Box>
											<PasswordInput
												sx={
													{
														...(RegistrationTextFieldSX as SxProps<Theme>),
														...(addressFormFieldsSX as SxProps<Theme>),
													} as SxProps<Theme>
												}
												required
												name="logonPassword"
												label={registerNLS.Password.t()}
												onBlur={handleOnBlur as () => void}
												error={!!errors.logonPassword}
												helperText={errors.logonPassword}
												value={registrationValues.logonPassword}
												autoFocus
												onChange={handleChange}
												inputProps={{
													maxLength: 100,
													pattern: escapeRegExp(registrationValues.logonPassword),
												}}
											/>
											{registrationValues.logonPassword ? (
												<PasswordValidations
													validations={{
														...passwordValidation(registrationValues.logonPassword),
														isMobile,
													}}
												/>
											) : null}
										</Box>
										<Button
											type="submit"
											variant="contained"
											sx={loginButtonSX}
											disabled={blockRegister}
											data-testid="button-register-submit"
											id="button-register-submit"
										>
											{'Continuar'}
										</Button>
									</Stack>
								</>
							))
							.defaultTo(() => null)}
					</Stack>
				</Paper>
				{!withoutLogin ? (
					<Paper sx={loginContainerSX({ isMobile })}>
						{isMobile ? <Divider sx={{ marginBottom: '15px' }} /> : null}
						<Stack alignItems="center" spacing={2}>
							<Typography sx={loginLabelSX}>{'¿Ya tienes cuenta?'}</Typography>
							<Linkable
								href={{
									pathname: RouteLocal.Login.route.t(),
									query: router.query.flow ? { flow: router.query.flow } : {},
								}}
								type="button"
								variant="contained"
								sx={loginIsButtonSX}
							>
								{'Inicia sesión'}
							</Linkable>
						</Stack>
					</Paper>
				) : null}
			</Grid>
		</Grid>
	);
};
